import { ExerciseNavigationHeader } from './ExerciseNavigationHeader';
import { AntDesign, Feather, Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useStoreState } from '@/stores/store';
import { useCallback, useMemo, useState } from 'react';
import { ExerciseControl } from './ExerciseControls';
import { HEADER_BTNS, TASKS } from './constants';
import { IconName } from '@/components/Icon';

interface ExcerciseControlButton {
  backgroundColor?: string;
  label: string;
  iconName: IconName | Record<string, IconName>;
  iconLibrary: string;
  isDark?: boolean;
  functionName: string;
  iconNameValue: string;
  functionParam?: string;
  notOnPress: boolean;
  pressIn: string;
  pressOut: string;
}

interface HeaderAufgabenProps {
  activeExerciseName: 'Verstehen' | 'Sprechen' | 'Lesen' | 'Schreiben';
  activeExerciseClass: 'word' | 'sentence';
  headerFunctions: Record<string, Function>;
  headerVariableIcons: Record<string, string>;
  onSkip: () => void;
  isCorrect: boolean;
  state: {
    zielwort: string;
  };
  showResultsModalChange: (value: boolean) => void;
  isSelf: boolean;
  selectedPerformance: string;
  goSelect: () => void;
  goHome: () => void;
  isRecording: boolean;
}

export const HeaderAufgaben = ({
  activeExerciseName,
  activeExerciseClass,
  headerFunctions,
  headerVariableIcons,
  onSkip: handlePressSkip,
  isCorrect,
  state,
  showResultsModalChange,
  isSelf,
  selectedPerformance,
  goSelect,
  goHome,
  isRecording = false,
}: HeaderAufgabenProps) => {
  const { goBack } = useNavigation();
  const [buttonClicked, setButtonClicked] = useState<null | number>(null);
  const { isTherapistDemo } = useStoreState((store) => store);

  const _HEADER_BTNS = useMemo(() => {
    if (activeExerciseClass == undefined || activeExerciseName == undefined)
      return [];
    const firstRes = HEADER_BTNS[activeExerciseClass];
    const secondRes = firstRes[activeExerciseName];
    return secondRes == undefined ? firstRes.defaultBtns : secondRes;
  }, [activeExerciseClass, activeExerciseName]);

  const handlePressHome = useCallback(() => {
    if (isSelf) {
      if (goSelect) goSelect();
      else goHome();
    } else {
      if (isTherapistDemo) {
        goBack();
      } else {
        showResultsModalChange(true);
      }
    }
  }, [goSelect]);

  const exerciseClassIcon = useMemo(() => {
    return (
      TASKS.default.find(
        (task) => task.exerciseSettingsKey == activeExerciseName,
      ) ?? {
        label: '',
        iconName: '',
        iconLibrary: '',
        exerciseSettingsKey: '',
      }
    );
  }, [activeExerciseName]);

  const mapExerciseControls = (
    exerciseControl: ExcerciseControlButton,
    index: number,
  ) => {
    const {
      backgroundColor,
      label,
      iconName,
      iconLibrary,
      isDark,
      functionName,
      iconNameValue,
      functionParam,
      notOnPress,
      pressIn,
      pressOut,
    } = exerciseControl;

    const realLabel = label === 'variable' ? state.zielwort : label;
    const onPress = headerFunctions[functionName];
    const onPressIn = headerFunctions[pressIn];
    const onPressOut = headerFunctions[pressOut];

    const derivedIconName =
      typeof iconName == 'string'
        ? iconName
        : iconName[headerVariableIcons[iconNameValue].toString()];

    let icon = null;

    switch (iconLibrary) {
      case 'Ionicons':
        icon = <Ionicons name={derivedIconName} />;
        break;
      case 'Feather':
        icon = <Feather name={derivedIconName} />;
        break;
      case 'AntDesign':
        icon = <AntDesign name={derivedIconName} />;
        break;

      default:
        break;
    }

    return (
      <ExerciseControl
        backgroundColor={backgroundColor}
        isDisabled={
          (index !== buttonClicked && buttonClicked !== null) || isRecording
        }
        isDark={isDark}
        highlighted={functionParam == selectedPerformance}
        key={index}
        onPress={
          !notOnPress && functionParam ? onPress(functionParam) : onPress
        }
        onPressIn={() => {
          onPressIn();
          setButtonClicked(index);
        }}
        onPressOut={() => {
          onPressOut();
          setButtonClicked(null);
        }}
        label={realLabel}
        icon={icon}
      />
    );
  };

  const exerciseRatingButtons = _HEADER_BTNS
    .filter((x: ExcerciseControlButton) => x.isDark)
    .map(mapExerciseControls);

  /** For sentences, we don't want to show the other light buttons to optimize space when zielsatz is clicked */
  const exerciseHelpButtons =
    activeExerciseClass === 'sentence' && state.zielwort !== 'Zielsatz'
      ? _HEADER_BTNS
          .filter((x: ExcerciseControlButton) => x.label === 'variable')
          .map(mapExerciseControls)
      : _HEADER_BTNS
          .filter((x: ExcerciseControlButton) => !x.isDark)
          .map(mapExerciseControls);

  return (
    <ExerciseNavigationHeader
      isSelf={isSelf}
      exerciseClass={exerciseClassIcon}
      onPressHome={handlePressHome}
      onPressSkip={handlePressSkip}
      skipTitle={isCorrect ? 'Weiter' : 'Überspringen'}
      exerciseRatingButtons={exerciseRatingButtons}
      exerciseHelpButtons={exerciseHelpButtons}
      activeExerciseClass={activeExerciseClass}
    />
  );
};
